import { useRecentBooked, useRecentlyCompleted, useUpcomingEvents } from '@/api/b2b/getEvents';
import { ScrollableLayout } from '@/components/Layout';
import Page from '@/components/Layout/Page';
import { Button } from '@/components/ui/elements/button';
import { PolicyGate } from '@/features/auth/authorization';
import { B2bStatsCards } from '@/features/b2b/components/B2bStatsCards';
import { EventColumn } from '@/features/b2b/components/EventColumn';
import { OpenLeadsColumn } from '@/features/b2b/components/OpenLeadsColumn';
import { PastCustomersColumn } from '@/features/b2b/components/PastCustomersColumn';
import { ProspectsColumn } from '@/features/b2b/components/ProspectsColumn';
import { useB2bLeadAlerts } from '@/features/b2b/hooks/useB2bLeadAlerts';
import { EventLookup } from '@/features/dashboard/components/EventLookup';
import { LeadLookup } from '@/features/dashboard/components/LeadLookup';
import { NewLeadForm } from '@/features/dashboard/components/NewLeadForm';
import { AuthContext } from '@/providers/auth';
import { Link } from '@tanstack/react-router';
import { useContext } from 'react';

export const B2bDashboard = () => {
  useB2bLeadAlerts();
  const { user } = useContext(AuthContext);

  return (
    <ScrollableLayout>
      <Page>
        <Page.Header className="mx-4">
          <Page.HeaderContent title={`Hi, ${user?.first_name}!`}>
            B2B Command Center · Non-Birthday Events
          </Page.HeaderContent>
          <Page.HeaderAction className="items-center gap-3">
            <PolicyGate policy="callcenter.manager">
              <Button asChild variant="outline">
                <Link to="/callcenter/team">Call Center Team View</Link>
              </Button>
            </PolicyGate>
          </Page.HeaderAction>
        </Page.Header>
        {/* Access is gated by the is_b2b flag in the dispatcher, not an ACL
            policy, so Page.Body is left policy-less (mirrors CallCenterDashboard). */}
        <Page.Body className="@container space-y-4 p-4 py-2" data-testid="b2b-dashboard">
          {/* Row 1: reused quick actions */}
          <div className="grid grid-cols-1 @4xl:grid-cols-2 gap-2">
            <PolicyGate policy="leads.create">
              <NewLeadForm />
            </PolicyGate>
            <div className="grid grid-cols-1 @lg:grid-cols-2 gap-2">
              <PolicyGate policy="leads.view">
                <LeadLookup />
              </PolicyGate>
              <PolicyGate policy="events.view">
                <EventLookup />
              </PolicyGate>
            </div>
          </div>

          {/* Rows 2 & 3: stats */}
          <B2bStatsCards />

          {/* Row 4: opportunities */}
          <div className="grid grid-cols-1 gap-2 @2xl:grid-cols-3">
            <OpenLeadsColumn />
            <PastCustomersColumn />
            <ProspectsColumn />
          </div>

          {/* Row 5: events */}
          <div className="grid grid-cols-1 gap-2 @2xl:grid-cols-3">
            <EventColumn
              title="Recently Booked"
              useData={useRecentBooked}
              emptyLabel="No recent bookings."
              testid="b2b-recent-booked"
            />
            <EventColumn
              title="Upcoming"
              useData={useUpcomingEvents}
              emptyLabel="No upcoming events."
              testid="b2b-upcoming"
            />
            <EventColumn
              title="Recently Completed"
              useData={useRecentlyCompleted}
              emptyLabel="No recently completed events."
              testid="b2b-completed"
            />
          </div>
        </Page.Body>
      </Page>
    </ScrollableLayout>
  );
};
